import { NgFor } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  NbButtonModule,
  NbCardModule,
  NbDialogRef,
  NbDialogService,
  NbIconModule,
  NbInputModule
} from '@nebular/theme';
import { Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { RouterState } from 'src/app/features/auth/redux/router-state';
import { Drb } from 'src/app/models/drb-models/drb';
import { NgxGenericModalComponent } from 'src/app/shared/ui/ngx-generic-modal/ngx-generic-modal.component';
import { DrbState, SaveDrbState } from '../../redux';

@Component({
  selector: 'app-manage-contributors',
  templateUrl: './manage-contributors.component.html',
  styleUrls: ['./manage-contributors.component.css'],
  imports: [NbCardModule, NbIconModule, NbButtonModule, FormsModule, NgFor, NbInputModule]
})
export class ManageContributorsComponent implements OnInit, OnDestroy {
  public username$: Observable<string> = inject(Store).select(RouterState.username);
  private unsubscribe: Subject<void> = new Subject();

  public drb: Drb;
  public contributors: string[];
  public username: string;
  public errorMessage: string;
  public max_contributors = 2;
  public bundle_name: string;

  constructor(
    protected ref: NbDialogRef<ManageContributorsComponent>,
    private store: Store,
    private dialogService: NbDialogService,
    private router: Router
  ) {}

  public ngOnInit() {
    this.bundle_name = this.router.url.split('/')[2];
    this.drb = this.store.selectSnapshot(DrbState.getByKey(this.bundle_name));
    this.username$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.username = data));
    if (this.contributors.length === 0) {
      this.contributors = Array(this.max_contributors).fill('');
    }
    if (this.contributors.length > 0 && this.contributors.length < this.max_contributors) {
      const numEmptyValues = this.max_contributors - this.contributors.length;
      const emptyValues = new Array(numEmptyValues).fill('');
      this.contributors = this.contributors.concat(emptyValues);
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public save() {
    const localDrb = JSON.parse(JSON.stringify(this.drb));

    if (this.contributors.every(this.verifyEmail)) {
      if (this.checkIfNoDuplicates()) {
        if (this.checkContributorOwner()) {
          localDrb.contributors = this.contributors.filter((x) => x !== '');
          localDrb.last_updated_by = this.username;
          localDrb.last_updated_ts = Math.floor(Date.now() / 1000);
          localDrb.bundle_name = this.bundle_name;

          this.store.dispatch(new SaveDrbState(localDrb, this.bundle_name));
          this.ref.close();
        } else {
          this.errorMessage = $localize`Action not allowed: User is already the Owner.`;
        }
      } else {
        this.errorMessage = $localize`Action not allowed: User is already a contributor.`;
      }
    } else {
      this.errorMessage = $localize`Please enter valid contributor email address. name@irobot.com`;
    }
  }

  public trackByIndex(index: number, obj: any): any {
    return index;
  }

  public verifyEmail(email) {
    return email.length === 0 || Boolean(email.match('^[\\w-\\.]+@irobot.com$'));
  }

  public close() {
    this.dialogService
      .open(NgxGenericModalComponent, {
        context: {
          title: $localize`Confirm Close`,
          message: $localize`Are you sure that you want to close the window? Any changes that you have made will be lost.`
        }
      })
      .onClose.subscribe((data) => {
        if (data) {
          this.ref.close();
        }
      });
  }

  public checkIfNoDuplicates() {
    const nonEmptyArray = this.contributors.filter((str) => str !== '');
    const set = new Set(nonEmptyArray);

    return set.size === nonEmptyArray.length;
  }

  public checkContributorOwner() {
    return !this.contributors.includes(this.drb.owner);
  }
}
